/**
 * A small ring buffer of "what went wrong recently" that an issue report can
 * attach without the user having to open DevTools: uncaught errors, unhandled
 * promise rejections, console.error calls, React render crashes (pushed by
 * ErrorBoundary) and non-2xx API responses (pushed by services/api.js).
 *
 * Install once at app start. Entries hold a timestamp, a kind, and a message
 * clipped to a few hundred characters — never request or response bodies, so a
 * report can't accidentally carry telemetry or credentials.
 */

const MAX_ENTRIES = 20;
const MAX_MESSAGE_CHARS = 600;

const entries = [];
let installed = false;

function clip(value) {
  const text = typeof value === 'string' ? value : safeStringify(value);
  return text.length > MAX_MESSAGE_CHARS ? `${text.slice(0, MAX_MESSAGE_CHARS)}…` : text;
}

function safeStringify(value) {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return typeof value === 'undefined' ? 'undefined' : JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

export function pushDiagnostic(kind, message, extra = null) {
  entries.push({
    at: new Date().toISOString(),
    kind,
    message: clip(message),
    ...(extra ? { extra } : {}),
  });
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
}

/** Newest last. Returns a copy so callers can't mutate the buffer. */
export function snapshotDiagnostics() {
  return entries.map(e => ({ ...e }));
}

/** Testing hook. */
export function clearDiagnostics() {
  entries.length = 0;
}

/** Called by services/api.js for any non-2xx response. Only the method,
 *  path (query string dropped — it can carry tokens) and status are kept. */
export function recordFailedRequest(method, url, status) {
  // Drop the origin and everything from the first `?` or `#`.
  const path = String(url).replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/[?#].*$/, '');
  pushDiagnostic('http', `${(method || 'GET').toUpperCase()} ${path} → ${status}`);
}

export function installDiagnosticsBuffer(target = typeof window !== 'undefined' ? window : null) {
  if (installed || !target) return;
  installed = true;

  target.addEventListener('error', event => {
    const err = event.error;
    const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : '';
    pushDiagnostic('error', `${err ? safeStringify(err) : event.message}${where}`,
      err?.stack ? { stack: clip(err.stack) } : null);
  });
  target.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    pushDiagnostic('rejection', safeStringify(reason),
      reason?.stack ? { stack: clip(reason.stack) } : null);
  });

  const original = target.console?.error;
  if (typeof original === 'function') {
    target.console.error = (...args) => {
      try {
        pushDiagnostic('console.error', args.map(safeStringify).join(' '));
      } catch { /* never let the buffer break logging */ }
      original.apply(target.console, args);
    };
  }
}
